'use client';

import { c as _c } from "react/compiler-runtime";
import { motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useMemo } from 'react';
import type { SidebarChatMessage } from './types/sidebar-chat-message';
import { getMessageTextContent } from './message-part-utils';
import { Button } from '@/components/ui/button';
import { BetterTooltip } from '@/components/ui/tooltip';
import { trackClientEvent } from '@/lib/analytics/posthog-client';
import { parseTourFooter, TOUR_BACK_TEXT, TOUR_END_TEXT, TOUR_NEXT_TEXT } from '@/lib/chat/tour-protocol';
import { usePrStore } from '@/lib/stores/pr-store';
const TOUR_CONTROL_CLASS = 'h-7 shrink-0 rounded-full text-xs font-normal transition-colors [&_svg]:size-3.5';

// Next stop is clicked at every stop, so it gets the emphasis and the
// rightmost slot, where wizards and pagers put the forward action. End tour
// is a quiet link at the far left: when it sat to the right of Next stop,
// users clicked it by habit.
const TOUR_NEXT_CLASS = `${TOUR_CONTROL_CLASS} gap-1 border border-zinc-700 bg-zinc-800 px-3 text-zinc-100 hover:border-zinc-600 hover:bg-zinc-700 hover:text-zinc-50`;
const TOUR_BACK_CLASS = `${TOUR_CONTROL_CLASS} w-7 border border-zinc-800/70 bg-zinc-900/45 px-0 text-zinc-300 hover:border-zinc-700 hover:bg-zinc-900/80 hover:text-zinc-100`;
const TOUR_END_CLASS = `${TOUR_CONTROL_CLASS} -ml-2 px-2 text-zinc-500 hover:bg-transparent hover:text-zinc-300`;
const TOUR_ACTIONS = {
  next: {
    text: TOUR_NEXT_TEXT,
    event: 'v2_chat_tour_next_clicked'
  },
  back: {
    text: TOUR_BACK_TEXT,
    event: 'v2_chat_tour_back_clicked'
  },
  end: {
    text: TOUR_END_TEXT,
    event: 'v2_chat_tour_end_clicked'
  }
} as const;
type TourAction = keyof typeof TOUR_ACTIONS;
type TourQuickReplyProps = {
  lastMessage: SidebarChatMessage | undefined;
  onSend: (text: string) => void;
};

/**
 * Renders the tour controls whenever the last assistant message ends with the
 * guided-tour footer: "End tour" on the left; the progress counter, "Previous
 * stop" (from stop 2) and "Next stop" on the right. The footer is the tour's
 * only state, so a missed parse degrades to the user typing "next" — never a
 * stuck tour.
 */
export function TourQuickReply(t0) {
  const $ = _c(32);
  const {
    lastMessage,
    onSend
  } = t0;
  const {
    owner,
    repo,
    number,
    installationId
  } = usePrStore();
  let t1;
  bb0: {
    if (lastMessage?.role !== "assistant") {
      t1 = null;
      break bb0;
    }
    let t2;
    if ($[0] !== lastMessage) {
      const text = getMessageTextContent(lastMessage);
      t2 = text ? parseTourFooter(text) : null;
      $[0] = lastMessage;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    t1 = t2;
  }
  const footer = t1;
  if (!footer) {
    return null;
  }
  let t2;
  if ($[2] !== footer.stop || $[3] !== footer.total || $[4] !== installationId || $[5] !== number || $[6] !== onSend || $[7] !== owner || $[8] !== repo) {
    t2 = action => {
      const {
        text: text_0,
        event
      } = TOUR_ACTIONS[action];
      trackClientEvent(event, installationId?.toString(), {
        owner,
        repo,
        pr_number: number,
        stop_index: footer.stop,
        stop_count: footer.total
      });
      onSend(text_0);
    };
    $[2] = footer.stop;
    $[3] = footer.total;
    $[4] = installationId;
    $[5] = number;
    $[6] = onSend;
    $[7] = owner;
    $[8] = repo;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const sendTourReply = t2;
  let t3;
  let t4;
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      opacity: 0,
      y: 4
    };
    t4 = {
      opacity: 1,
      y: 0
    };
    t5 = {
      duration: 0.15
    };
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t3 = $[10];
    t4 = $[11];
    t5 = $[12];
  }
  let t6;
  if ($[13] !== sendTourReply) {
    t6 = <Button type="button" variant="ghost" size="sm" onClick={() => sendTourReply("end")} className={TOUR_END_CLASS}>End tour</Button>;
    $[13] = sendTourReply;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const t7 = `${footer.stop}/${footer.total}`;
  let t8;
  if ($[15] !== t7) {
    t8 = <span aria-hidden={true} className="font-mono text-[10px] tracking-wide text-zinc-500">{t7}</span>;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== footer.stop || $[18] !== sendTourReply) {
    t9 = footer.stop > 1 ? <BetterTooltip content="Previous stop"><Button type="button" variant="ghost" size="sm" aria-label="Previous stop" onClick={() => sendTourReply("back")} className={TOUR_BACK_CLASS}><ChevronLeftIcon /></Button></BetterTooltip> : null;
    $[17] = footer.stop;
    $[18] = sendTourReply;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== sendTourReply) {
    t10 = () => sendTourReply("next");
    $[20] = sendTourReply;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <ChevronRightIcon className="-mr-1" />;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t10) {
    t12 = <Button type="button" variant="ghost" size="sm" onClick={t10} className={TOUR_NEXT_CLASS}>Next stop{t11}</Button>;
    $[23] = t10;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t12 || $[26] !== t8 || $[27] !== t9) {
    t13 = <div className="flex shrink-0 items-center gap-1.5">{t8}{t9}{t12}</div>;
    $[25] = t12;
    $[26] = t8;
    $[27] = t9;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t13 || $[30] !== t6) {
    t14 = <motion.div initial={t3} animate={t4} transition={t5} data-testid="chat-quick-replies" className="flex min-w-0 items-center justify-between gap-3">{t6}{t13}</motion.div>;
    $[29] = t13;
    $[30] = t6;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  return t14;
}